import { Theme, ThemeProvider, useTheme } from "@mui/material/styles"
import {
    makeStyles as makeTssStyles,
    withStyles as withTssStyles,
} from "tss-react/mui-compat"

type StyleValue<Props> = unknown | ((props: Props) => unknown)
type StyleObject<Props> = Record<string, StyleValue<Props>>
type Styles<ThemeType, Props, ClassKey extends string> =
    | Record<
          ClassKey,
          StyleObject<Props> | ((props: Props) => StyleObject<Props>)
      >
    | ((
          theme: ThemeType,
      ) => Record<
          ClassKey,
          StyleObject<Props> | ((props: Props) => StyleObject<Props>)
      >)

function resolveStyleValue<Props>(
    value: StyleValue<Props>,
    props: Props,
    classes: Record<string, string>,
): unknown {
    if (typeof value === "function") {
        return (value as (props: Props) => unknown)(props)
    }
    if (Array.isArray(value)) {
        return value.map((item) => resolveStyleValue(item, props, classes))
    }
    if (value && typeof value === "object") {
        return Object.fromEntries(
            Object.entries(value).map(([key, nestedValue]) => [
                key.replace(/\$([A-Za-z0-9_-]+)/g, (_, classKey) =>
                    classes[classKey]
                        ? `.${classes[classKey]}`
                        : `$${classKey}`,
                ),
                resolveStyleValue(nestedValue, props, classes),
            ]),
        )
    }
    return value
}

export function makeStyles<
    ThemeType = Theme,
    Props = any,
    ClassKey extends string = string,
>(styles: Styles<ThemeType, Props, ClassKey>) {
    const useTssStyles = makeTssStyles<Props>()((theme, props, classes) => {
        const styleObject =
            typeof styles === "function"
                ? styles(theme as unknown as ThemeType)
                : styles
        return resolveStyleValue(styleObject, props, classes) as Record<
            ClassKey,
            any
        >
    })

    return (props?: Props): Record<string, string> =>
        useTssStyles((props ?? {}) as Props).classes as Record<string, string>
}

export function withStyles(
    styles: Record<string, any> | ((theme: Theme) => Record<string, any>),
) {
    return (Component: any): any =>
        withTssStyles(Component, ((
            theme: Theme,
            props: unknown,
            classes: Record<string, string>,
        ) =>
            resolveStyleValue(
                typeof styles === "function" ? styles(theme) : styles,
                props,
                classes,
            )) as any)
}

export const createStyles = <T>(styles: T): T => styles

export type WithStyles<T> = {
    classes: Record<
        keyof (T extends (...args: any[]) => infer Result ? Result : T),
        string
    >
}

export { ThemeProvider, useTheme }

export default makeStyles
